import { useState, useCallback, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { SettingsProvider } from '../contexts/SettingsContext';
import { OnboardingProvider } from '../contexts/OnboardingContext';
import { Sidebar } from './Sidebar';
import { MobileHeader } from './MobileHeader';
import { SuspendedScreen } from './SuspendedScreen';
import { Toast, type ToastVariant } from './ui/Toast';
import { AIAgentProvider, AISidebar, AISidebarToggle } from '../ai';
import { BadgeUnlockedToast } from './gamification';
import { TourOverlay } from './onboarding';
import type { AgentToastProps } from '../ai/types/agent';
import type { UserBadge } from '../types/gamification';

interface ToastState {
  message: string;
  variant: ToastVariant;
  linkText?: string;
  linkTo?: string;
  duration?: number;
}

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireCompleteProfile?: boolean;
  showSidebar?: boolean;
}

export function ProtectedRoute({
  children,
  requireCompleteProfile = true,
  showSidebar = true
}: ProtectedRouteProps) {
  const { user, loading, isProfileComplete, isSuspended, newlyEarnedBadges, clearNewlyEarnedBadges } = useAuth();
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [toast, setToast] = useState<ToastState | null>(null);
  const [currentBadgeToast, setCurrentBadgeToast] = useState<UserBadge | null>(null);
  const [badgeQueue, setBadgeQueue] = useState<UserBadge[]>([]);

  // Toast handler for AI Agent
  const handleShowToast = useCallback((props: AgentToastProps) => {
    setToast({
      message: props.message,
      variant: props.variant || 'success',
      linkText: props.linkText,
      linkTo: props.linkTo,
      duration: props.duration,
    });
  }, []);

  // Process newly earned badges
  useEffect(() => {
    if (newlyEarnedBadges.length > 0) {
      setBadgeQueue((prev) => [...prev, ...newlyEarnedBadges]);
      clearNewlyEarnedBadges();
    }
  }, [newlyEarnedBadges, clearNewlyEarnedBadges]);

  // Show badges from queue one by one
  useEffect(() => {
    if (!currentBadgeToast && badgeQueue.length > 0) {
      const [next, ...rest] = badgeQueue;
      setCurrentBadgeToast(next);
      setBadgeQueue(rest);
    }
  }, [currentBadgeToast, badgeQueue]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bt-bg">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-t-transparent" style={{ borderColor: 'var(--bt-accent)', borderTopColor: 'transparent' }}></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Show suspended screen if user is suspended
  if (isSuspended) {
    return <SuspendedScreen />;
  }

  if (requireCompleteProfile && !isProfileComplete && location.pathname !== '/profile-setup') {
    return <Navigate to="/profile-setup" replace />;
  }

  if (!showSidebar) {
    return <>{children}</>;
  }

  return (
    <SettingsProvider>
      <AIAgentProvider onShowToast={handleShowToast}>
        <OnboardingProvider>
          <div className="bt-bg transition-colors">
            <MobileHeader onMenuClick={() => setIsSidebarOpen(true)} />
            <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
            <div className="lg:pl-64 min-h-screen pt-16 lg:pt-0">
              <div className="p-4 lg:p-8">
                {children}
              </div>
            </div>

            {/* AI Agent */}
            <AISidebar />
            <AISidebarToggle />

            {/* Onboarding Tour Overlay */}
            <TourOverlay />

            {/* Global Toast */}
            {toast && (
              <Toast
                message={toast.message}
                variant={toast.variant}
                linkText={toast.linkText}
                linkTo={toast.linkTo}
                duration={toast.duration}
                onClose={() => setToast(null)}
              />
            )}

            {/* Badge Unlocked Toast */}
            {currentBadgeToast && (
              <BadgeUnlockedToast
                badge={currentBadgeToast}
                onClose={() => setCurrentBadgeToast(null)}
              />
            )}
          </div>
        </OnboardingProvider>
      </AIAgentProvider>
    </SettingsProvider>
  );
}
